import {
  hashKey,
  isCancelledError,
  type DefaultError,
  type Mutation,
  type Query,
} from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { isAbortError } from "@/lib/abort";
import { RestError } from "@/models/client/RestResponse";

/** How long a query that keeps failing stays quiet after it was logged. */
export const QUERY_ERROR_LOG_INTERVAL_MS = 5 * 60 * 1000;

const lastLoggedAt = new WeakMap<Query<unknown, unknown, unknown>, number>();

/**
 * Failures that are not news here: a cancelled fetch was abandoned on purpose — React Query's own
 * cancellation, or an `AbortError` from a signal the hook passed in itself, as the debounced table
 * searches do (LAR-585) — and a synthetic 401 was already logged by `restClient` where the token
 * fetch failed (LAR-361). Only a {@link RestError} says it is one — hooks that throw a plain
 * `Error` are logged regardless.
 */
const isAccountedFor = (error: unknown): boolean =>
  isCancelledError(error) ||
  isAbortError(error) ||
  (error instanceof RestError && error.clientAuthFailure !== undefined);

const statusOf = (error: unknown) =>
  error instanceof RestError ? { status: error.status } : {};

/**
 * `QueryCache` `onError`: one log per failed query, after its retries (LAR-520).
 *
 * `restClient` logs only token failures, so a query whose hook did not log was invisible in App
 * Insights. A query that keeps failing — a poll, or a page remounted over and over — is logged at
 * most once per {@link QUERY_ERROR_LOG_INTERVAL_MS}, so one broken endpoint cannot flood the
 * exceptions table the way the ResizeObserver loop did (LAR-435).
 */
export const logQueryError = (
  error: DefaultError,
  query: Query<unknown, unknown, unknown>
): void => {
  if (isAccountedFor(error)) return;

  const now = Date.now();
  const last = lastLoggedAt.get(query);
  if (last !== undefined && now - last < QUERY_ERROR_LOG_INTERVAL_MS) return;
  lastLoggedAt.set(query, now);

  logger.error("Query failed", {
    operation: "QueryCache.onError",
    error,
    queryKey: query.queryHash,
    ...statusOf(error),
  });
};

/**
 * `MutationCache` `onError`: one log per failed mutation (LAR-520). Each failure is a separate user
 * action, so none are skipped. The variables stay out: they carry what the user typed or uploaded.
 */
export const logMutationError = (
  error: DefaultError,
  _variables: unknown,
  _onMutateResult: unknown,
  mutation: Mutation<unknown, unknown, unknown>
): void => {
  if (isAccountedFor(error)) return;

  const { mutationKey } = mutation.options;
  logger.error("Mutation failed", {
    operation: "MutationCache.onError",
    error,
    ...(mutationKey ? { mutationKey: hashKey(mutationKey) } : {}),
    ...statusOf(error),
  });
};
